import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/layout/Header";
import { stripeApi, type CreatorEarnings } from "../lib/api";
import { ArrowLeftIcon } from "../components/ui/Icons";

export function EarningsPage() {
  const [earnings, setEarnings] = useState<CreatorEarnings | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await stripeApi.getEarnings();
        setEarnings(data);
      } catch (err) {
        console.error("Failed to load earnings:", err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const formatPrice = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const handleDashboard = async () => {
    try {
      const result = await stripeApi.dashboardLink();
      window.open(result.url, "_blank");
    } catch (err) {
      console.error("Failed to get dashboard link:", err);
    }
  };

  return (
    <div className="min-h-screen bg-base">
      <Header />
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-1/4 top-0 h-[600px] w-[600px] rounded-full bg-amber/5 blur-[120px]" />
        <div className="absolute -left-1/4 bottom-0 h-[600px] w-[600px] rounded-full bg-purple-500/5 blur-[120px]" />
        <div className="absolute inset-0 bg-grid opacity-20" />
      </div>

      <main className="relative pt-24">
        <div className="mx-auto max-w-5xl px-4 pb-20 sm:px-6 lg:px-8">
          <Link
            to="/dashboard"
            className="mb-6 inline-flex items-center gap-2 text-sm text-muted transition-colors hover:text-secondary"
          >
            <ArrowLeftIcon size={16} />
            Back to Dashboard
          </Link>

          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="font-display text-3xl font-bold tracking-tight text-primary">
                Creator <span className="text-amber">Earnings</span>
              </h1>
              <p className="mt-2 text-muted">Track your plugin sales and revenue</p>
            </div>
            <button onClick={handleDashboard} className="btn-primary self-start">
              Stripe Dashboard
            </button>
          </div>

          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card-glow animate-pulse p-6">
                  <div className="mb-2 h-4 w-24 rounded bg-elevated/50" />
                  <div className="h-8 w-32 rounded bg-elevated/50" />
                </div>
              ))}
            </div>
          ) : !earnings ? (
            <div className="card-glow p-8 text-center">
              <p className="text-muted">Unable to load earnings data</p>
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="mb-8 grid gap-4 sm:grid-cols-3">
                <div className="card-glow p-6">
                  <p className="mb-1 font-mono text-sm text-muted">Total Earnings</p>
                  <p className="font-display text-2xl font-bold text-success">
                    {formatPrice(earnings.totalEarnings)}
                  </p>
                </div>
                <div className="card-glow p-6">
                  <p className="mb-1 font-mono text-sm text-muted">Total Sales</p>
                  <p className="font-display text-2xl font-bold text-primary">
                    {earnings.totalSales}
                  </p>
                </div>
                <div className="card-glow p-6">
                  <p className="mb-1 font-mono text-sm text-muted">Avg per Sale</p>
                  <p className="font-display text-2xl font-bold text-primary">
                    {earnings.totalSales > 0
                      ? formatPrice(earnings.totalEarnings / earnings.totalSales)
                      : "$0.00"}
                  </p>
                </div>
              </div>

              {/* Per-Plugin Breakdown */}
              {earnings.perPlugin.length > 0 && (
                <div className="mb-8">
                  <h2 className="mb-4 font-display text-lg font-bold text-primary">By Plugin</h2>
                  <div className="card-glow overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-elevated">
                          <th className="px-6 py-3 text-left font-mono text-xs font-medium uppercase text-muted">
                            Plugin
                          </th>
                          <th className="px-6 py-3 text-right font-mono text-xs font-medium uppercase text-muted">
                            Sales
                          </th>
                          <th className="px-6 py-3 text-right font-mono text-xs font-medium uppercase text-muted">
                            Earnings
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {earnings.perPlugin.map((plugin) => (
                          <tr key={plugin.pluginId} className="border-b border-elevated/50">
                            <td className="px-6 py-4">
                              <Link
                                to={`/plugin/${plugin.slug}`}
                                className="font-medium text-primary hover:text-amber"
                              >
                                {plugin.name}
                              </Link>
                            </td>
                            <td className="px-6 py-4 text-right font-mono text-sm text-muted">
                              {plugin.sales}
                            </td>
                            <td className="px-6 py-4 text-right font-mono text-sm text-success">
                              {formatPrice(plugin.earnings)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Recent Sales */}
              {earnings.recentSales.length > 0 && (
                <div>
                  <h2 className="mb-4 font-display text-lg font-bold text-primary">Recent Sales</h2>
                  <div className="card-glow overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-elevated">
                          <th className="px-6 py-3 text-left font-mono text-xs font-medium uppercase text-muted">
                            Plugin
                          </th>
                          <th className="px-6 py-3 text-right font-mono text-xs font-medium uppercase text-muted">
                            Amount
                          </th>
                          <th className="px-6 py-3 text-right font-mono text-xs font-medium uppercase text-muted">
                            Your Earnings
                          </th>
                          <th className="px-6 py-3 text-right font-mono text-xs font-medium uppercase text-muted">
                            Date
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {earnings.recentSales.map((sale, i) => (
                          <tr key={i} className="border-b border-elevated/50">
                            <td className="px-6 py-4 text-sm text-primary">{sale.pluginName}</td>
                            <td className="px-6 py-4 text-right font-mono text-sm text-muted">
                              {formatPrice(sale.amount)}
                            </td>
                            <td className="px-6 py-4 text-right font-mono text-sm text-success">
                              {formatPrice(sale.creatorEarnings)}
                            </td>
                            <td className="px-6 py-4 text-right font-mono text-sm text-muted">
                              {new Date(sale.date).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {earnings.totalSales === 0 && (
                <div className="card-glow flex flex-col items-center justify-center py-16 text-center">
                  <p className="mb-2 font-display text-lg font-bold text-primary">No sales yet</p>
                  <p className="max-w-sm text-sm text-muted">
                    Set prices on your plugins to start earning. Head to your dashboard to get
                    started.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
